/**
 * WebP and Srcset Lazy Loading Enhancement with Tiered Loading
 * Optimizes image loading with WebP support, responsive srcset, and prioritized loading tiers
 *
 * Three-Tier Loading Strategy:
 * - Critical (rootMargin: 0px): Above fold images, load immediately
 * - Near (rootMargin: 200px): Near viewport, load ~200px before visible
 * - Far (rootMargin: 400px): Far below fold, load ~400px before visible
 */

(function() {
  'use strict';

  // Check if browser supports IntersectionObserver for efficient lazy loading
  if (!('IntersectionObserver' in window)) {
    // Fallback: load all images immediately for older browsers
    loadAllImages();
    return;
  }

  // Tiered Loading Configuration
  const LOADING_TIERS = {
    'critical': {
      rootMargin: '0px 0px',
      threshold: 0.01,
      description: 'Critical above-fold images'
    },
    'near': {
      rootMargin: '200px 0px',
      threshold: 0.01,
      description: 'Images near viewport'
    },
    'far': {
      rootMargin: '400px 0px',
      threshold: 0.01,
      description: 'Images far below fold'
    },
    'default': {
      rootMargin: '50px 0px',
      threshold: 0.01,
      description: 'Default tier for images without explicit tier'
    }
  };

  // Image loading handler (shared across all observers)
  function handleImageLoad(entries, observer) {
    entries.forEach(function(entry) {
      if (entry.isIntersecting) {
        const element = entry.target;

        // Handle picture > source elements (WebP)
        if (element.tagName === 'PICTURE') {
          const sources = element.querySelectorAll('source[data-srcset]');
          sources.forEach(function(source) {
            if (source.dataset.srcset) {
              source.srcset = source.dataset.srcset;
              delete source.dataset.srcset;
            }
          });
        }

        // Handle img elements
        const img = element.tagName === 'IMG' ? element : element.querySelector('img');
        if (img) {
          // Load srcset first if available
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            delete img.dataset.srcset;
          }

          // Then load src
          if (img.dataset.src) {
            img.src = img.dataset.src;
            delete img.dataset.src;
          }

          // Add loaded class for animations
          img.classList.add('lazy-loaded');
          img.classList.remove('lazy2', 'lazyload_image');

          // Set loading attribute to trigger CSS opacity transition
          img.setAttribute('loading', 'eager');

          // Emit custom event for tracking (optional monitoring)
          if (typeof CustomEvent !== 'undefined') {
            const tier = element.dataset.loadingTier || img.dataset.loadingTier || 'default';
            const loadEvent = new CustomEvent('lazyImageLoaded', {
              detail: { tier: tier, element: img }
            });
            document.dispatchEvent(loadEvent);
          }
        }

        // Stop observing this element
        observer.unobserve(element);
      }
    });
  }

  // Create separate observers for each tier
  const observers = {
    critical: new IntersectionObserver(handleImageLoad, LOADING_TIERS.critical),
    near: new IntersectionObserver(handleImageLoad, LOADING_TIERS.near),
    far: new IntersectionObserver(handleImageLoad, LOADING_TIERS.far),
    default: new IntersectionObserver(handleImageLoad, LOADING_TIERS.default)
  };

  // Helper function to check if element is in viewport
  function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  // Helper function to load an image immediately
  function loadImageImmediately(element) {
    // Handle picture > source elements (WebP)
    if (element.tagName === 'PICTURE') {
      const sources = element.querySelectorAll('source[data-srcset]');
      sources.forEach(function(source) {
        if (source.dataset.srcset) {
          source.srcset = source.dataset.srcset;
          delete source.dataset.srcset;
        }
      });
    }

    // Handle img elements
    const img = element.tagName === 'IMG' ? element : element.querySelector('img');
    if (img) {
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        delete img.dataset.srcset;
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
      img.classList.add('lazy-loaded');
      img.classList.remove('lazy2', 'lazyload_image');

      // Set loading attribute to trigger CSS opacity transition
      img.setAttribute('loading', 'eager');
    }
  }

  // Observe all lazy images with tier-based loading
  function observeImages() {
    // Find all lazy images (both in pictures and standalone)
    const allLazyImages = document.querySelectorAll('img.lazy2, img.lazyload_image');

    allLazyImages.forEach(function(img) {
      // Get the tier from the image's data attribute
      const tier = img.dataset.loadingTier || 'default';
      const observer = observers[tier] || observers.default;

      // Check if image is inside a picture element
      const picture = img.closest('picture');
      const elementToObserve = picture || img;

      // For critical tier or images already in viewport, load immediately
      if (tier === 'critical' || isInViewport(elementToObserve)) {
        loadImageImmediately(elementToObserve);
      } else {
        // Otherwise observe for lazy loading
        observer.observe(elementToObserve);
      }
    });
  }

  // Fallback function for older browsers
  function loadAllImages() {
    const allSources = document.querySelectorAll('source[data-srcset]');
    allSources.forEach(function(source) {
      if (source.dataset.srcset) {
        source.srcset = source.dataset.srcset;
      }
    });

    const allImages = document.querySelectorAll('img[data-src], img[data-srcset]');
    allImages.forEach(function(img) {
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
      }
      img.classList.add('lazy-loaded');
      img.classList.remove('lazy2', 'lazyload_image');

      // Set loading attribute to trigger CSS opacity transition
      img.setAttribute('loading', 'eager');
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      observeImages();
      // Debug: Log initialization
      if (window.location.search.includes('debug=lazy')) {
        console.log('Lazy loader initialized. Found images:',
          document.querySelectorAll('img.lazy2, img.lazyload_image').length);
      }
    });
  } else {
    observeImages();
    // Debug: Log initialization
    if (window.location.search.includes('debug=lazy')) {
      console.log('Lazy loader initialized. Found images:',
        document.querySelectorAll('img.lazy2, img.lazyload_image').length);
    }
  }

  // Re-observe new images added dynamically (for infinite scroll, etc.)
  const mutationObserver = new MutationObserver(function(mutations) {
    let shouldObserve = false;
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        shouldObserve = true;
      }
    });
    if (shouldObserve) {
      observeImages();
    }
  });

  // Observe body for dynamically added images (optimized configuration)
  if (document.body) {
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,        // Don't watch attribute changes for better performance
      characterData: false      // Don't watch text changes
    });
  }

})();
